/**
 * ExecutionProgress
 *
 * A glanceable, log-free live progress block for a flow run. Lives at the top
 * of the Test Run Details "Overview" tab so a user can tell a running test is
 * making progress without reading the raw log stream.
 *
 * Everything here is derived from data the Firestore listener already streams
 * (status / startedAt / logs / currentNode) — no extra fetch. There is
 * deliberately NO percentage: flows have loops, conditionals, and parallel
 * branches, so a denominator would be a lie. We show a phase stepper, the
 * current node, a completed-step count, a ticking elapsed timer, and an
 * indeterminate bar while live.
 */

import { useEffect, useMemo, useState } from 'react';
import { cn, formatDuration } from '@/lib/utils';
import { CheckCircle2, Circle, Loader2, XCircle, AlertCircle, AlertTriangle } from 'lucide-react';
import type { ExecutionLog } from './ExecutionLogViewer';
import type { SetupStepView } from '@/hooks/useExecutionLogs';
import { firstStepTime, wholeRunMs } from '@/utils/runDuration';

export interface ExecutionProgressProps {
  status: string | null;
  isLive: boolean;
  startedAt?: Date | null;
  completedAt?: Date | null;
  /** Total duration in ms (from result.duration) — preferred when present. */
  durationMs?: number;
  logs: ExecutionLog[];
  currentNode?: string | null;
  error?: string | null;
  /** Queue wait-state (while the run is waiting for a free modem). */
  pendingReason?: string;
  pendingDetail?: string;
  lastDispatchAttemptAt?: Date | null;
  attemptCount?: number;
  /** Named run steps: setup sub-steps (#381), then the flow's steps and the release (#389). */
  setupSteps?: SetupStepView[];
  className?: string;
}

type Phase = 'setup' | 'execution' | 'cleanup';
type StepState = 'pending' | 'active' | 'done' | 'error';

const PHASE_ORDER: { key: Phase; label: string }[] = [
  { key: 'setup', label: 'Setup' },
  { key: 'execution', label: 'Execution' },
  { key: 'cleanup', label: 'Cleanup' },
];

// One colour per phase (#389), shared by the stepper and the step list so a
// step reads as setup, the test itself, or cleanup at a glance. Step state
// keeps its own icon colours (green done, red failed, amber warning).
const PHASE_STYLE: Record<Phase, { rail: string; text: string; dot: string }> = {
  setup: { rail: 'border-accent-blue', text: 'text-accent-blue', dot: 'bg-accent-blue' },
  execution: { rail: 'border-accent-purple', text: 'text-accent-purple', dot: 'bg-accent-purple' },
  cleanup: { rail: 'border-status-offline', text: 'text-status-offline', dot: 'bg-status-offline' },
};

// Mirrors ExecutionLogViewer.detectPhase — kept local so this component doesn't
// depend on the viewer's internals. Keep the marker lists in sync.
function detectPhase(message: string): Phase | null {
  const m = message.toUpperCase();
  if (
    m.includes('RESOURCE CLEANUP') ||
    m.includes('RELEASED') ||
    m.includes('EXECUTION COMPLETE') ||
    m.includes('EXECUTION FAILED')
  ) {
    return 'cleanup';
  }
  if (
    m.includes('EXECUTION STARTED') ||
    m.includes('ESIM PROFILE ALLOCATION') ||
    m.includes('MODEM ALLOCATION') ||
    m.includes('LOCATIONS RESOLVED') ||
    m.includes('CLUSTER EXECUTION STARTED')
  ) {
    return 'setup';
  }
  if (
    m.includes('NODE:') ||
    m.includes('AT COMMAND:') ||
    m.includes('FLOW EXECUTION') ||
    m.includes('RESPONSE:') ||
    m.includes('PARALLEL EXECUTION')
  ) {
    return 'execution';
  }
  return null;
}

function formatElapsed(ms: number): string {
  if (ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const mnt = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return h > 0 ? `${h}:${pad(mnt)}:${pad(s)}` : `${pad(mnt)}:${pad(s)}`;
}

function formatAgo(ms: number): string {
  const sec = Math.round(ms / 1000);
  if (sec < 2) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  return `${min}m ${sec % 60}s ago`;
}

export function ExecutionProgress({
  status,
  isLive,
  startedAt,
  completedAt,
  durationMs,
  logs,
  currentNode,
  error,
  pendingDetail,
  lastDispatchAttemptAt,
  attemptCount,
  setupSteps,
  className,
}: ExecutionProgressProps) {
  // A queued run is still "in progress" (it's actively waiting for a modem),
  // so treat it as live for the ticking timer, indeterminate bar, and stepper.
  const live = isLive || status === 'queued';

  // Tick once a second while live so the elapsed timer + "last checked" stay
  // fresh even when no new log arrives — this is the "something is happening"
  // signal that keeps a queued/initializing run from ever reading as frozen.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [live]);

  const failed = status === 'failed';
  const cancelled = status === 'cancelled';
  const completed = status === 'completed';

  const { presentPhases, activeIndex, currentLabel, completedSteps, lastEventAt } = useMemo(() => {
    const present = new Set<Phase>();
    let lastNode: string | null = null;
    let doneCount = 0;
    let lastTs: number | undefined;

    for (const step of setupSteps ?? []) present.add(step.phase);
    for (const log of logs) {
      const phase = detectPhase(log.message);
      if (phase) present.add(phase);

      const nodeMatch = /NODE:\s*(.+?)\s*$/.exec(log.message);
      if (nodeMatch) lastNode = nodeMatch[1].replace(/^[^\w(]+/, '').trim();

      if (/completed in/i.test(log.message)) doneCount += 1;

      const ts = log.timestamp instanceof Date ? log.timestamp.getTime() : new Date(log.timestamp).getTime();
      if (!Number.isNaN(ts)) lastTs = lastTs === undefined ? ts : Math.max(lastTs, ts);
    }

    const idx = PHASE_ORDER.reduce(
      (acc, p, i) => (present.has(p.key) ? i : acc),
      -1,
    );

    return {
      presentPhases: present,
      activeIndex: idx,
      currentLabel: lastNode,
      completedSteps: doneCount,
      lastEventAt: lastTs,
    };
  }, [logs, setupSteps]);

  // Before the flow starts (eSIM move, locks) there is no startedAt yet: time
  // the run from its first step instead of showing 00:00.
  const firstStepAt = useMemo(() => firstStepTime(setupSteps), [setupSteps]);

  const elapsedMs = useMemo(() => {
    // Finished: the whole run, including setup before the flow (eSIM move).
    if (!live) {
      const whole = wholeRunMs({ durationMs, startedAt, completedAt, firstStepAt });
      if (whole !== undefined) return whole;
    }
    // Whichever came first, so the timer doesn't jump back when the flow
    // starts after a long setup.
    const starts = [startedAt?.getTime(), firstStepAt].filter((t): t is number => typeof t === 'number');
    const start = starts.length ? Math.min(...starts) : undefined;
    if (start === undefined) return 0;
    const end = live ? now : completedAt?.getTime() ?? now;
    return end - start;
  }, [durationMs, startedAt, firstStepAt, completedAt, live, now]);

  // "Waiting" = in progress but nothing has executed yet (queued / dispatching).
  // This is the state the old UI rendered as a blank "No logs available" pane.
  const waiting = live && logs.length === 0;

  const stepStateFor = (i: number): StepState => {
    if (!presentPhases.has(PHASE_ORDER[i].key)) {
      // Not reached yet. On a finished run, a never-reached phase reads "pending"
      // (e.g. a run that failed in Setup never had a Cleanup phase).
      return 'pending';
    }
    if (live) {
      if (i < activeIndex) return 'done';
      if (i === activeIndex) return 'active';
      return 'pending';
    }
    // Finished: mark the last reached phase error on failure, else all done.
    if ((failed || cancelled) && i === activeIndex) return 'error';
    return 'done';
  };

  // Headline status pill
  const statusPill = (() => {
    if (failed) return { dot: 'bg-status-error', text: 'Failed', cls: 'text-status-error' };
    if (cancelled) return { dot: 'bg-muted-foreground', text: 'Cancelled', cls: 'text-muted-foreground' };
    if (completed) return { dot: 'bg-status-success', text: 'Completed', cls: 'text-status-success' };
    if (status === 'paused') return { dot: 'bg-amber-500', text: 'Paused', cls: 'text-amber-600 dark:text-amber-400' };
    // Queued but already working on the run (eSIM move, locks): say so.
    if (status === 'queued' && setupSteps?.length) return { dot: 'bg-accent-blue animate-pulse', text: 'Setting up', cls: 'text-accent-blue' };
    if (status === 'queued') return { dot: 'bg-muted-foreground animate-pulse', text: 'Queued', cls: 'text-muted-foreground' };
    return { dot: 'bg-status-success animate-pulse', text: 'Running', cls: 'text-status-success' };
  })();

  // Finished: count the listed steps of every phase when the run has them.
  const finishedSteps = setupSteps?.length ? setupSteps.filter((s) => s.state !== 'skipped').length : completedSteps;
  const stepLabel = live
    ? `Step ${completedSteps + (currentLabel ? 1 : 0) || 1}`
    : `${finishedSteps} step${finishedSteps === 1 ? '' : 's'}`;
  // While setup is still running (no flow node yet), name the setup step
  // instead of "Step 1" (#381).
  const runningSetup = setupSteps?.find((s) => s.state === 'running');
  const liveLabel = currentLabel || runningSetup?.label || null;

  return (
    <div className={cn('rounded-lg border bg-muted/30 p-4 space-y-3', className)}>
      {/* Status + elapsed */}
      <div className="flex items-center justify-between gap-2">
        <div className={cn('flex items-center gap-2 text-sm font-medium', statusPill.cls)}>
          <span className={cn('h-2 w-2 rounded-full', statusPill.dot)} />
          {statusPill.text}
        </div>
        <div className="font-mono text-sm text-muted-foreground tabular-nums">
          {formatElapsed(elapsedMs)}
          {live ? ' elapsed' : ''}
        </div>
      </div>

      {/* Progress bar — indeterminate while live (no honest %), solid when done */}
      <div className="relative h-1.5 overflow-hidden rounded-full bg-muted">
        {live ? (
          <div className="absolute inset-y-0 left-0 w-full rounded-full bg-primary animate-indeterminate" />
        ) : (
          <div
            className={cn(
              'absolute inset-y-0 left-0 w-full rounded-full',
              failed || cancelled ? 'bg-status-error' : 'bg-status-success',
            )}
          />
        )}
      </div>

      {/* Phase stepper */}
      <div className="flex items-center">
        {PHASE_ORDER.map((p, i) => {
          const state = stepStateFor(i);
          return (
            <div key={p.key} className="flex flex-1 items-center last:flex-none">
              <div className="flex items-center gap-1.5">
                {state === 'done' && <CheckCircle2 className="h-4 w-4 text-status-success" />}
                {state === 'active' && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                {state === 'error' && <XCircle className="h-4 w-4 text-status-error" />}
                {state === 'pending' && <Circle className="h-4 w-4 text-muted-foreground/40" />}
                <span
                  className={cn(
                    'text-xs',
                    state === 'pending' ? 'text-muted-foreground/50' : PHASE_STYLE[p.key].text,
                    state === 'active' && 'font-medium',
                  )}
                >
                  {p.label}
                </span>
              </div>
              {i < PHASE_ORDER.length - 1 && (
                <div
                  className={cn(
                    'mx-2 h-px flex-1',
                    i < activeIndex ? 'bg-status-success/40' : 'bg-border',
                  )}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Run steps, grouped and coloured by phase: setup sub-steps (#381) —
          moving / downloading the eSIM, waiting for the network, the locks —
          then the flow's own steps and the release (#389), each with its
          state, duration and outcome. */}
      {setupSteps && setupSteps.length > 0 && (
        <div className="space-y-2">
          {PHASE_ORDER.map(({ key: phase, label }) => {
            const steps = setupSteps.filter((s) => s.phase === phase);
            if (steps.length === 0) return null;
            return (
              <div key={phase} className="space-y-1">
                <div className={cn('flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide', PHASE_STYLE[phase].text)}>
                  <span className={cn('h-1.5 w-1.5 rounded-full', PHASE_STYLE[phase].dot)} />
                  {label}
                </div>
                <ol className={cn('space-y-1.5 border-l-2 pl-3', PHASE_STYLE[phase].rail)}>
                  {steps.map((step) => {
                    const start = step.startedAt?.getTime();
                    const end = step.endedAt?.getTime() ?? (step.state === 'running' ? now : undefined);
                    const dur = start !== undefined && end !== undefined ? end - start : undefined;
                    return (
                      <li key={step.key} className="flex items-start gap-2 text-xs">
                        {step.state === 'running' && <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-primary" />}
                        {step.state === 'done' && <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-success" />}
                        {step.state === 'warning' && <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-warning" />}
                        {step.state === 'failed' && <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-error" />}
                        {step.state === 'skipped' && <Circle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className={cn('text-foreground', step.state === 'running' && 'font-medium')}>
                              {step.label}
                            </span>
                            {dur !== undefined && (
                              <span className="shrink-0 font-mono tabular-nums text-muted-foreground">
                                {formatElapsed(dur)}
                              </span>
                            )}
                          </div>
                          {step.detail && (
                            <p
                              className={cn(
                                'leading-relaxed',
                                step.state === 'failed' ? 'text-status-error'
                                  : step.state === 'warning' ? 'text-status-warning'
                                  : 'text-muted-foreground',
                              )}
                            >
                              {step.detail}
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            );
          })}
        </div>
      )}

      {/* Waiting in queue — the run is alive but no node has executed yet.
          Shows WHY (pendingDetail) + a ticking "last checked" + retry count so
          a queued run reads as actively working, never frozen. */}
      {waiting ? (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-sm">
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
            <span className="font-medium text-foreground">
              {pendingDetail ? 'Waiting in queue' : 'Starting…'}
            </span>
          </div>
          {pendingDetail && (
            <p className="text-xs leading-relaxed text-muted-foreground">{pendingDetail}</p>
          )}
          {(typeof attemptCount === 'number' || lastDispatchAttemptAt) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground tabular-nums">
              {typeof attemptCount === 'number' && attemptCount > 0 && (
                <span>Attempt {attemptCount}</span>
              )}
              {lastDispatchAttemptAt && (
                <span>Last checked {formatAgo(now - lastDispatchAttemptAt.getTime())}</span>
              )}
            </div>
          )}
        </div>
      ) : live ? (
        <div className="flex items-center justify-between gap-2 text-sm">
          <div className="flex min-w-0 items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground shrink-0">{stepLabel}</span>
            {liveLabel && (
              <span className={cn('truncate text-foreground', currentLabel && 'font-mono')} title={liveLabel}>
                · {liveLabel}
              </span>
            )}
          </div>
          {lastEventAt !== undefined && (
            <span className="shrink-0 text-xs text-muted-foreground">
              last event {formatAgo(now - lastEventAt)}
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {failed ? (
            <>
              <AlertCircle className="h-3.5 w-3.5 shrink-0 text-status-error" />
              <span className="truncate" title={error || undefined}>
                {error || 'Execution failed'}
              </span>
            </>
          ) : (
            <span>
              {completed ? 'Finished' : statusPill.text} · {formatDuration(elapsedMs)} · {stepLabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
